// FILE: src/components/ExamPrepChecklist.js
//
// "Exam Prep" checklist — self-attested, toggleable prep milestones beyond
// practice: required training hours (both certs), and the PMP application +
// exam scheduling (PMP only, with deadlines relative to the target exam date).
//
// Distinct from earned achievements (auto-awarded, permanent). These are the
// learner's to check on/off. Backed by prepTasksService + migration 025.
//
// Two presentations:
//   • full (default) — coach-layout header (PM Pixel notepad) + the checklist.
//     Used on the Milestones page.
//   • compact — a small card (no avatar) for the dashboard.
//
// examId selects which cert's tasks to show; the recommended-by dates use the
// learner's target_exam_date (only when their target cert matches examId).

import React, { useCallback, useEffect, useState } from 'react';
import CoachLayout from '../coach/CoachLayout';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { loadPrepTasks, setPrepTaskComplete } from '../../services/prepTasksService';
import styles from './ExamPrepChecklist.module.css';

export default function ExamPrepChecklist({ examId, compact = false }) {
  const { profile } = useLearnerAuth();
  // The target exam date only applies when the learner's target cert is the
  // one we're showing prep for.
  const targetExamDate =
    profile && profile.target_exam_id === examId ? (profile.target_exam_date || null) : null;

  const [tasks, setTasks]     = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const reload = useCallback(async () => {
    if (!examId) { setTasks([]); return; }
    setTasks(await loadPrepTasks(examId, { targetExamDate }));
  }, [examId, targetExamDate]);

  useEffect(() => { let alive = true; (async () => { const t = await loadPrepTasks(examId, { targetExamDate }); if (alive) setTasks(t); })(); return () => { alive = false; }; }, [examId, targetExamDate]);

  async function toggle(task) {
    setBusyKey(task.key);
    const next = !task.completed;
    // Optimistic flip.
    setTasks((prev) => prev.map((t) => (t.key === task.key ? { ...t, completed: next } : t)));
    await setPrepTaskComplete(task.key, examId, next);
    await reload(); // refresh completion + due/overdue
    setBusyKey(null);
  }

  if (!tasks || tasks.length === 0) return null;

  const list = (
    <ul className={styles.list} data-testid="exam-prep-list">
      {tasks.map((t) => (
        <li key={t.key} className={`${styles.item} ${t.completed ? styles.done : ''}`}>
          <label className={styles.label}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={t.completed}
              disabled={busyKey === t.key}
              onChange={() => toggle(t)}
              data-testid={`prep-task-${t.key}`}
            />
            <span className={styles.body}>
              <span className={styles.title}>{t.title}</span>
              {!compact && <span className={styles.desc}>{t.description}</span>}
              {t.dueDate && (
                <span className={t.overdue ? styles.overdue : styles.due}>
                  {t.overdue ? 'Overdue — was recommended by ' : 'Recommended by '}
                  {formatDate(t.dueDate)}
                </span>
              )}
              {t.needsTargetDate && (
                <span className={styles.hint}>Set a target exam date to see the recommended deadline.</span>
              )}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );

  if (compact) {
    return (
      <section className={styles.cardCompact} data-testid="exam-prep-checklist">
        <h3 className={styles.headingCompact}>Exam Prep</h3>
        {list}
      </section>
    );
  }

  return (
    <section className={styles.section} data-testid="exam-prep-checklist">
      <CoachLayout pose="notepad" heightPx={150}>
        <p className={styles.eyebrow}>Exam Prep</p>
        <h2 className={styles.heading}>A few steps beyond practice</h2>
        <p className={styles.lead}>
          Certification isn’t only the exam. Check these off as you complete them —
          they’re yours to track, and you can un-check anytime.
        </p>
      </CoachLayout>
      {list}
    </section>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) { return iso; }
}
